import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, ApiError, keys, useMeta } from '../../api.ts';
import { bandSchema, modeSchema } from '../../../shared/schemas.ts';
import { Field, Loading, PageHeader, useDocumentTitle, useToast } from '../../components/ui.tsx';

/** Poisto: jos kohteeseen liittyy QSO:ita, palvelin vastaa 409 ja pyydetään vahvistus */
function useForceDelete(base: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<{ key: string; message: string } | null>(null);
  const m = useMutation({
    mutationFn: ({ key, force }: { key: string; force: boolean }) => api.del(`${base}/${encodeURIComponent(key)}${force ? '?force=1' : ''}`),
    onSuccess: () => {
      setPending(null);
      toast.ok('Poistettu');
      void qc.invalidateQueries({ queryKey: keys.meta });
    },
    onError: (err, v) => {
      if (err instanceof ApiError && err.status === 409) setPending({ key: v.key, message: err.message });
      else toast.error(err);
    },
  });
  return { pending, cancel: () => setPending(null), remove: (key: string, force = false) => m.mutate({ key, force }) };
}

export function BandsModesPage() {
  useDocumentTitle('Bandit ja moodit');
  const meta = useMeta();
  const qc = useQueryClient();
  const toast = useToast();
  const bandDel = useForceDelete('/admin/bands');
  const modeDel = useForceDelete('/admin/modes');

  const bandForm = useForm<z.input<typeof bandSchema>, unknown, z.output<typeof bandSchema>>({ resolver: zodResolver(bandSchema) });
  const modeForm = useForm<z.input<typeof modeSchema>, unknown, z.output<typeof modeSchema>>({
    resolver: zodResolver(modeSchema),
    defaultValues: { mode: '', parentMode: '' },
  });
  const addBand = useMutation({
    mutationFn: (d: z.output<typeof bandSchema>) => api.post('/admin/bands', d),
    onSuccess: () => {
      bandForm.reset({ band: '' });
      void qc.invalidateQueries({ queryKey: keys.meta });
    },
    onError: toast.error,
  });
  const addMode = useMutation({
    mutationFn: (d: z.output<typeof modeSchema>) => api.post('/admin/modes', d),
    onSuccess: () => {
      modeForm.reset({ mode: '', parentMode: '' });
      void qc.invalidateQueries({ queryKey: keys.meta });
    },
    onError: toast.error,
  });

  if (meta.isLoading) return <Loading />;
  const parents = meta.data!.modes.filter((m) => !m.parentMode);

  const ForceBox = ({ d }: { d: ReturnType<typeof useForceDelete> }) =>
    d.pending && (
      <div className="alert alert-warn" style={{ marginTop: 12 }}>
        {d.pending.message}{' '}
        <button className="btn btn-sm btn-danger" onClick={() => d.remove(d.pending!.key, true)}>
          Poista silti
        </button>{' '}
        <button className="btn btn-sm btn-ghost" onClick={d.cancel}>
          Peruuta
        </button>
      </div>
    );

  return (
    <div className="page">
      <PageHeader title="Bandit ja moodit" />
      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">
            <h2>Bandit</h2>
          </div>
          <div className="chips">
            {meta.data!.bands.map((b) => (
              <span key={b.band} className="chip">
                {b.label}
                <button className="btn btn-ghost btn-sm" style={{ height: 20, padding: '0 4px' }} aria-label={`Poista ${b.label}`} onClick={() => bandDel.remove(String(b.band))}>
                  ×
                </button>
              </span>
            ))}
          </div>
          <ForceBox d={bandDel} />
          <form className="row" style={{ marginTop: 16, alignItems: 'flex-end' }} onSubmit={bandForm.handleSubmit((d) => addBand.mutate(d))}>
            <Field label="Uusi band (metreinä, esim. 60 tai 0,23)" error={bandForm.formState.errors.band} htmlFor="nband">
              <input id="nband" className="input mono" {...bandForm.register('band')} />
            </Field>
            <button className="btn btn-primary" type="submit">
              Lisää
            </button>
          </form>
        </div>

        <div className="card">
          <div className="card-title">
            <h2>Moodit</h2>
          </div>
          <div className="stack">
            {parents.map((p) => (
              <div key={p.mode}>
                <div className="row">
                  <strong className="mono">{p.mode}</strong>
                  <button className="btn btn-ghost btn-sm" onClick={() => modeDel.remove(p.mode)} title="Poistaa myös alimoodit">
                    Poista
                  </button>
                </div>
                <div className="chips" style={{ marginTop: 6 }}>
                  {meta.data!.modes
                    .filter((m) => m.parentMode === p.mode)
                    .map((m) => (
                      <span key={m.mode} className="chip">
                        {m.mode}
                        <button className="btn btn-ghost btn-sm" style={{ height: 20, padding: '0 4px' }} aria-label={`Poista ${m.mode}`} onClick={() => modeDel.remove(m.mode)}>
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </div>
          <ForceBox d={modeDel} />
          <form className="row" style={{ marginTop: 16, alignItems: 'flex-end' }} onSubmit={modeForm.handleSubmit((d) => addMode.mutate(d))}>
            <Field label="Uusi moodi" error={modeForm.formState.errors.mode} htmlFor="nmode">
              <input id="nmode" className="input mono" style={{ width: 140 }} {...modeForm.register('mode')} />
            </Field>
            <Field label="Päämoodi" htmlFor="pmode">
              <select id="pmode" style={{ width: 160 }} {...modeForm.register('parentMode')}>
                <option value="">(itse päämoodi)</option>
                {parents.map((p) => (
                  <option key={p.mode} value={p.mode}>
                    {p.mode}
                  </option>
                ))}
              </select>
            </Field>
            <button className="btn btn-primary" type="submit">
              Lisää
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
